import { useCallback, useMemo, useState } from 'react';
import type { CoreMembership, CoreOrganization, CoreRole, UserLabel } from '@/lib/zifekCore';
import { createCoreMembership, searchUsers, updateCoreMembership } from '@/lib/zifekCore';
import { PERMISSION_GROUPS, roleMeta } from '@/lib/corePermissions';
import { logAudit } from '@/lib/coreAudit';
import { createWorkerAccount } from '@/lib/workerAccounts';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  org: CoreOrganization;
  memberships: CoreMembership[];
  roles: CoreRole[];
  users: Record<number, UserLabel>;
  actorName?: string | null;
  actorId?: number | null;
  ownerType?: string | null;
  ownerTypecompte?: number | null;
  commerceId?: number | null;
  currency?: string;
  actionCounts?: Record<number, number>;
  onChanged: () => void;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

type AddMode = 'existing' | 'new';

function groupPermKeys(groupKey: string): string[] {
  const g = PERMISSION_GROUPS.find((x) => x.key === groupKey);
  return g ? g.permissions.map((p) => p.key) : [];
}

function appCount(perms: string[]): number {
  if (perms.includes('*')) return PERMISSION_GROUPS.length;
  return PERMISSION_GROUPS.filter((g) => g.permissions.some((p) => perms.includes(p.key))).length;
}

function randomPassword(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 10; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function TeamAccessTab({
  org, memberships, roles, users, actorName, actorId,
  ownerType, ownerTypecompte, commerceId, currency = 'MAD', actionCounts, onChanged,
}: Props) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CoreMembership | null>(null);
  const [mode, setMode] = useState<AddMode>('existing');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserLabel[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<UserLabel | null>(null);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [roleId, setRoleId] = useState<number | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const defaultRole = useMemo(
    () => roles.find((r) => r.role_key === 'employee') || roles[0] || null,
    [roles],
  );

  const runSearch = useCallback(async (q: string) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const found = await searchUsers(q);
      const memberIds = new Set(memberships.map((m) => m.user_id));
      setResults(found.filter((u) => !memberIds.has(u.id)));
    } finally {
      setSearching(false);
    }
  }, [memberships]);

  const openAdd = () => {
    setEditing(null);
    setMode('existing');
    setQuery('');
    setResults([]);
    setPicked(null);
    setNewName('');
    setNewEmail('');
    setNewPhone('');
    setNewPassword('');
    const r = defaultRole;
    setRoleId(r?.id ?? null);
    setSelectedPerms(r?.permissions && r.permissions.length ? [...r.permissions] : []);
    setError('');
    setOpen(true);
  };

  const openEdit = (m: CoreMembership) => {
    setEditing(m);
    setPicked(null);
    setRoleId(m.role_id ?? null);
    const current = Array.isArray(m.permissions) ? m.permissions : [];
    setSelectedPerms([...current]);
    setError('');
    setOpen(true);
  };

  const onPickRole = (id: number) => {
    setRoleId(id);
    const r = roles.find((x) => x.id === id);
    if (r?.permissions && r.permissions.length) setSelectedPerms([...r.permissions]);
  };

  const togglePerm = (key: string) => {
    setSelectedPerms((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));
  };

  const toggleGroup = (groupKey: string) => {
    const keys = groupPermKeys(groupKey);
    setSelectedPerms((prev) => {
      const allOn = keys.every((k) => prev.includes(k));
      return allOn ? prev.filter((p) => !keys.includes(p)) : Array.from(new Set([...prev, ...keys]));
    });
  };

  const save = async () => {
    const chosenRole = roles.find((r) => r.id === roleId) || defaultRole;
    if (!chosenRole) { setError('Aucun rôle disponible.'); return; }
    if (selectedPerms.length === 0) { setError('Choisissez au moins une application à autoriser.'); return; }
    if (!editing && mode === 'existing' && !picked) {
      setError('Sélectionnez un compte Zifek à ajouter comme travailleur.'); return;
    }
    if (!editing && mode === 'new') {
      if (!newName.trim()) { setError('Renseignez le nom du travailleur.'); return; }
      if (!newEmail.trim()) { setError("Renseignez l'email du travailleur."); return; }
      if (newPassword.trim().length < 6) { setError('Le mot de passe doit contenir au moins 6 caractères.'); return; }
      if (!commerceId) { setError('Commerce introuvable.'); return; }
    }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateCoreMembership(editing.id, {
          roleKey: chosenRole.role_key, roleLabel: chosenRole.name, roleId: chosenRole.id,
          permissions: selectedPerms,
        });
        await logAudit({
          tenantId: org.tenant_id, organizationId: org.id, userId: actorId ?? null, userName: actorName ?? null,
          action: 'update', resourceType: 'member', resourceId: String(editing.user_id),
          after: { permissions: selectedPerms, role: chosenRole.name },
        }).catch(() => {});
      } else {
        let userId: number | null = null;
        let label = '';
        if (mode === 'existing' && picked) {
          userId = picked.id;
          label = picked.name || picked.email || `#${picked.id}`;
        } else {
          userId = await createWorkerAccount({
            name: newName, email: newEmail, telephone: newPhone, password: newPassword,
            commerceId: commerceId as number,
            ownerType: ownerType || 'Commerçant',
            ownerTypecompte: Number(ownerTypecompte) || 0,
            currency,
          });
          label = newName.trim();
        }
        await createCoreMembership({
          tenantId: org.tenant_id, organizationId: org.id, userId,
          roleKey: chosenRole.role_key, roleLabel: chosenRole.name, roleId: chosenRole.id,
          permissions: selectedPerms,
        });
        await logAudit({
          tenantId: org.tenant_id, organizationId: org.id, userId: actorId ?? null, userName: actorName ?? null,
          action: 'create', resourceType: 'member', resourceId: String(userId),
          after: { name: label, permissions: selectedPerms, role: chosenRole.name, accountCreated: mode === 'new' },
        }).catch(() => {});
      }
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (m: CoreMembership) => {
    const suspending = m.status === 'active';
    setBusyId(m.id);
    try {
      await updateCoreMembership(m.id, { status: suspending ? 'suspended' : 'active' });
      await logAudit({
        tenantId: org.tenant_id, organizationId: org.id, userId: actorId ?? null, userName: actorName ?? null,
        action: suspending ? 'delete' : 'restore', resourceType: 'member', resourceId: String(m.user_id),
        after: { status: suspending ? 'suspended' : 'active' },
      }).catch(() => {});
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          Ajoutez des travailleurs et choisissez <strong className="text-foreground-700">précisément les applications</strong> auxquelles ils ont accès.
        </p>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-user-add-line"></i>
          Ajouter un travailleur
        </button>
      </div>

      {memberships.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-group-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun travailleur pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {memberships.map((m) => {
            const u = users[m.user_id];
            const meta = roleMeta(m.role, m.role_label);
            const perms = Array.isArray(m.permissions) ? m.permissions : [];
            const inactive = m.status !== 'active';
            return (
              <div key={m.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 ${inactive ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {(u?.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">
                        {u?.name || `Utilisateur #${m.user_id}`}
                        {m.is_primary && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">Propriétaire</span>}
                      </div>
                      <div className="text-[11px] text-foreground-400 truncate">
                        {u?.user_name ? `@${u.user_name}` : ''}{u?.email ? ` · ${u.email}` : ''}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${inactive ? 'bg-foreground-100 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
                    {inactive ? 'Suspendu' : 'Actif'}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.className}`}>
                    <i className={meta.icon}></i>{meta.label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium">
                    <i className="ri-apps-2-line"></i>{appCount(perms)} application{appCount(perms) > 1 ? 's' : ''}
                  </span>
                  {(() => {
                    const recent = actionCounts?.[m.user_id] ?? 0;
                    return (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${recent > 0 ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-500'}`}
                        title="Nombre d'actions enregistrées ces 30 derniers jours"
                      >
                        <i className="ri-history-line"></i>{recent} action{recent > 1 ? 's' : ''} · 30j
                      </span>
                    );
                  })()}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {PERMISSION_GROUPS.filter((g) => g.permissions.some((p) => perms.includes(p.key))).map((g) => (
                    <span key={g.key} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-medium">
                      <i className={g.icon}></i>{g.label}
                    </span>
                  ))}
                  {perms.includes('*') && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 font-medium">
                      <i className="ri-shield-star-line"></i>Accès complet
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-background-200/60">
                  <button
                    onClick={() => openEdit(m)}
                    disabled={m.is_primary === true}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-shield-user-line"></i> Gérer les accès
                  </button>
                  {!m.is_primary && (
                    <button
                      onClick={() => toggleStatus(m)}
                      disabled={busyId === m.id}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 ${inactive ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                    >
                      <i className={inactive ? 'ri-user-follow-line' : 'ri-user-unfollow-line'}></i>
                      {inactive ? 'Réactiver' : 'Suspendre'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={editing ? 'Gérer les accès du travailleur' : 'Ajouter un travailleur'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

          <div className="space-y-4">
            {!editing && (
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setMode('existing')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'existing' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                >
                  <i className="ri-link"></i> Compte existant
                </button>
                <button
                  type="button"
                  onClick={() => setMode('new')}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'new' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                >
                  <i className="ri-user-add-line"></i> Créer un compte
                </button>
              </div>
            )}

            {!editing && mode === 'existing' && (
              <div>
                <span className="block text-xs font-medium text-foreground-600 mb-1">Compte du travailleur</span>
                {picked ? (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-background-200/70 bg-primary-50">
                    <span className="flex items-center gap-2 text-sm text-foreground-800 min-w-0">
                      <i className="ri-user-3-line text-primary-600"></i>
                      <span className="truncate">{picked.name || `#${picked.id}`}</span>
                      {picked.email && <span className="text-xs text-foreground-500 truncate">· {picked.email}</span>}
                    </span>
                    <button onClick={() => setPicked(null)} className="text-xs text-foreground-500 hover:text-foreground-800 underline cursor-pointer whitespace-nowrap">Changer</button>
                  </div>
                ) : (
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                    <input className={`${INPUT} pl-9`} value={query} onChange={(e) => runSearch(e.target.value)} placeholder="Rechercher un compte (nom, pseudo, email)…" />
                  </div>
                )}
                {searching && <div className="flex items-center justify-center py-3"><i className="ri-loader-4-line animate-spin text-primary-500"></i></div>}
                {!searching && results.length > 0 && (
                  <div className="mt-2 border border-background-200/70 rounded-md divide-y divide-background-200/60 max-h-44 overflow-y-auto">
                    {results.map((u) => (
                      <button key={u.id} onClick={() => setPicked(u)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-background-100 transition-colors cursor-pointer">
                        <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-xs font-bold text-foreground-600 flex-shrink-0">
                          {(u.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground-900 truncate">{u.name || `#${u.id}`}</div>
                          <div className="text-xs text-foreground-400 truncate">{u.user_name ? `@${u.user_name}` : ''}{u.email ? ` · ${u.email}` : ''}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-foreground-400 mt-1.5">Le travailleur doit déjà posséder un compte Zifek.</p>
              </div>
            )}

            {!editing && mode === 'new' && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Nom complet</span>
                    <input className={INPUT} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ex : Sara Bennani" />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-foreground-600 mb-1">Téléphone (optionnel)</span>
                    <input className={INPUT} value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Ex : +212 6 00 00 00 00" />
                  </label>
                </div>
                <label className="block">
                  <span className="block text-xs font-medium text-foreground-600 mb-1">Email (identifiant de connexion)</span>
                  <input type="email" className={INPUT} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Ex : sara@maboutique.com" />
                </label>
                <div>
                  <span className="block text-xs font-medium text-foreground-600 mb-1">Mot de passe</span>
                  <div className="flex items-center gap-2">
                    <input className={INPUT} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="6 caractères minimum" />
                    <button
                      type="button"
                      onClick={() => setNewPassword(randomPassword())}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i> Générer
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1.5">Communiquez ces identifiants au travailleur : il se connecte avec cet email et ce mot de passe.</p>
                </div>
              </div>
            )}

            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-1">Rôle</span>
              <select className={INPUT} value={roleId ?? ''} onChange={(e) => onPickRole(Number(e.target.value))}>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-foreground-600">Applications autorisées</span>
                <span className="text-[11px] text-foreground-400">{appCount(selectedPerms)} sélectionnée(s)</span>
              </div>
              <div className="space-y-2 max-h-[44vh] overflow-y-auto pr-1">
                {PERMISSION_GROUPS.map((g) => {
                  const keys = g.permissions.map((p) => p.key);
                  const allOn = keys.every((k) => selectedPerms.includes(k));
                  const someOn = keys.some((k) => selectedPerms.includes(k));
                  return (
                    <div key={g.key} className={`rounded-lg border p-3 transition-colors ${someOn ? 'border-primary-200 bg-primary-50/40' : 'border-background-200/70'}`}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.key)}
                        className="w-full flex items-center gap-3 text-left cursor-pointer"
                      >
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${allOn ? 'bg-primary-500 border-primary-500 text-background-50' : someOn ? 'bg-primary-100 border-primary-300 text-primary-700' : 'border-background-300/60 text-transparent'}`}>
                          <i className={allOn ? 'ri-check-line text-xs' : someOn ? 'ri-subtract-line text-xs' : 'ri-check-line text-xs'}></i>
                        </span>
                        <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                          <i className={g.icon}></i>
                        </span>
                        <span className="text-sm font-semibold text-foreground-800 flex-1 min-w-0 truncate">{g.label}</span>
                      </button>
                      <div className="mt-2 ml-8 flex flex-wrap gap-x-4 gap-y-1.5">
                        {g.permissions.map((p) => (
                          <label key={p.key} className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedPerms.includes(p.key)}
                              onChange={() => togglePerm(p.key)}
                              className="w-3.5 h-3.5 rounded border-background-300/60 accent-primary-500 cursor-pointer"
                            />
                            {p.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}